import type { TFunction } from 'i18next';
import { Check } from 'lucide-react';

import { Badge } from '@/components/ui/badge';

import type {
  ReceiveHistoryTableData,
  ReceiveQuantityDto,
  ReceiveTemperatureRangeDto,
  ReceiveDangerousGoodDto,
  ReceiveSubprojectDto
} from '@/entities/receive';
import { formatDisplayDate } from '@/shared/lib';
import type { ColumnPreset, DataTableColumn } from '@/shared/ui/data-table';
import { TemperatureRangeBadge } from '@/shared/ui/temperature-range-badge';

import { ReceiveHistoryAttachmentsCell } from '../ui/receive-history-attachments-cell';

const EMPTY_CELL = '-';

/** Per-line quantities as stacked `lineReference: qty unit` rows. */
const renderQuantities = (quantities: ReceiveQuantityDto[] | undefined) => {
  if (!quantities?.length) return EMPTY_CELL;

  return (
    <div className="flex flex-col gap-0.5">
      {quantities.map((quantity, index) => (
        <span key={`${quantity.lineReference}-${index}`} title={quantity.productDescription}>
          {quantity.lineReference}: {quantity.quantity} {quantity.unit}
        </span>
      ))}
    </div>
  );
};

const renderTemperatureRanges = (ranges: ReceiveTemperatureRangeDto[] | undefined) => {
  if (!ranges?.length) return EMPTY_CELL;

  return (
    <div className="flex flex-col gap-0.5">
      {ranges.map((range, index) => (
        <TemperatureRangeBadge
          key={`${range.minTemp}-${range.maxTemp}-${index}`}
          min={range.minTemp}
          max={range.maxTemp}
        />
      ))}
    </div>
  );
};

const renderDangerousGoods = (goods: ReceiveDangerousGoodDto[] | undefined) => {
  if (!goods?.length) return EMPTY_CELL;

  return (
    <div className="flex flex-col gap-0.5">
      {goods.map((good, index) => (
        <span key={`${good.unId}-${index}`} title={good.classDescription}>
          {good.unId}
          {good.description ? ` — ${good.description}` : ''}
        </span>
      ))}
    </div>
  );
};

const renderSubprojects = (subprojects: ReceiveSubprojectDto[] | undefined) => {
  if (!subprojects?.length) return EMPTY_CELL;

  return subprojects.map((subproject) => subproject.code || subproject.description || EMPTY_CELL).join(', ');
};

/**
 * Receive History columns — JSF parity: the default-visible set and order
 * match the legacy screen; the detail ("disposition") columns are hidden by
 * default and surfaced via Customize Columns / the Medical View preset.
 */
export const getReceiveHistoryColumns = (t: TFunction): DataTableColumn<ReceiveHistoryTableData>[] => [
  {
    id: 'receiveDate',
    accessorKey: 'receiveDate',
    header: t('receive.receivedate.label', 'Actual Date of Receipt'),
    description: t('receive.history.columns.receiveDate'),
    width: 'w-[110px]',
    // SR Created column styling; formatDisplayDate keeps the shared app format.
    cell: (row) => <div className="text-xs text-muted-foreground">{formatDisplayDate(row.receiveDate)}</div>,
    enableSorting: true,
    sortFieldId: 'receiveDate'
  },
  {
    id: 'consignmentNumber',
    accessorKey: 'consignmentNumber',
    header: t('journey.participatingRequestsStr.label', 'Consignments'),
    description: t('receive.history.columns.consignmentNumber'),
    width: 'w-[130px]',
    cell: (row) => <span className="font-mono text-sm">{row.consignmentNumber}</span>,
    enableSorting: true,
    sortFieldId: 'consignmentNumber'
  },
  {
    id: 'receivedAt',
    accessorKey: 'receivedAt',
    header: t('receive.receivedat.label', 'Received at Location'),
    description: t('receive.history.columns.receivedAt'),
    width: 'w-[250px]',
    cell: (row) => row.receivedAt || EMPTY_CELL,
    enableSorting: true,
    sortFieldId: 'receivedAt'
  },
  {
    id: 'cancellable',
    accessorKey: 'cancellable',
    header: t('receive.canellable.short', 'Cancellable'),
    description: t('receive.canellable.desc', 'This receive can be cancelled'),
    width: 'w-[90px]',
    cell: (row) => (row.cancellable ? <Check className="h-4 w-4" aria-hidden /> : EMPTY_CELL),
    enableSorting: false
  },
  {
    id: 'receiveKind',
    accessorKey: 'receiveKind',
    header: t('journeyType.description.label', 'Journey Type'),
    description: t('receive.history.columns.receiveKind'),
    width: 'w-[120px]',
    cell: (row) =>
      row.receiveKind === 'CUSTOMER'
        ? t('receive.customer.label', 'Customer Delivery')
        : t('receive.internal.label', 'Internal Transfer'),
    enableSorting: true,
    sortFieldId: 'journeyType'
  },
  {
    id: 'convoyName',
    accessorKey: 'convoyName',
    header: t('convoy.name.desc', 'Convoy'),
    description: t('receive.history.columns.convoyName'),
    width: 'w-[120px]',
    cell: (row) => row.convoyName || EMPTY_CELL,
    enableSorting: true,
    sortFieldId: 'convoyName'
  },
  {
    id: 'vehicleType',
    accessorKey: 'vehicleType',
    header: t('journey.vehicleType.label', 'Vehicle Type'),
    description: t('receive.history.columns.vehicleType'),
    width: 'w-[120px]',
    cell: (row) => row.vehicleType || EMPTY_CELL,
    enableSorting: true,
    sortFieldId: 'vehicleType'
  },
  {
    id: 'transportCompany',
    accessorKey: 'transportCompany',
    header: t('journey.transportCompany.label', 'Transport Company'),
    description: t('receive.history.columns.transportCompany'),
    width: 'w-[150px]',
    cell: (row) => row.transportCompany || EMPTY_CELL,
    enableSorting: true,
    sortFieldId: 'transportCompany'
  },
  {
    id: 'comments',
    header: t('receive.comments.label', 'Comments'),
    description: t('receive.history.columns.comments'),
    width: 'w-[160px]',
    cell: (row) =>
      row.comments?.length ? (
        <div className="flex flex-wrap gap-1">
          {row.comments.map((comment, index) => (
            <Badge key={index} variant="outline" className="max-w-[240px]" title={comment.text}>
              <span className="truncate">{comment.text}</span>
            </Badge>
          ))}
        </div>
      ) : (
        EMPTY_CELL
      ),
    enableSorting: false
  },
  {
    id: 'documentNumber',
    accessorKey: 'documentNumber',
    header: t('receive.document.label', 'Transport Documentation Number'),
    description: t('receive.history.columns.documentNumber'),
    width: 'w-[150px]',
    cell: (row) => row.documentNumber || EMPTY_CELL,
    enableSorting: true,
    sortFieldId: 'documentNumber'
  },
  {
    id: 'totalReceivedWeight',
    accessorKey: 'totalReceivedWeight',
    header: t('consignment.columns.weightKg', 'Weight (kg)'),
    description: t('receive.history.columns.totalReceivedWeight'),
    width: 'w-[100px]',
    cell: (row) => (typeof row.totalReceivedWeight === 'number' ? row.totalReceivedWeight.toFixed(2) : EMPTY_CELL),
    enableSorting: false
  },
  {
    id: 'totalReceivedVolume',
    accessorKey: 'totalReceivedVolume',
    header: t('consignment.columns.volumeM3', 'Volume (m³)'),
    description: t('receive.history.columns.totalReceivedVolume'),
    width: 'w-[100px]',
    cell: (row) => (typeof row.totalReceivedVolume === 'number' ? row.totalReceivedVolume.toFixed(2) : EMPTY_CELL),
    enableSorting: false
  },

  /* --- disposition-detail columns (hidden by default, JSF parity) --------- */
  {
    id: 'inventoryQuantities',
    header: t('tracking.servicereq.unitqty.label', 'Inventory Unit Quantity'),
    description: t('receive.history.columns.inventoryQuantities'),
    width: 'w-[180px]',
    cell: (row) => renderQuantities(row.inventoryQuantities),
    enableSorting: false,
    visible: false
  },
  {
    id: 'bundleQuantities',
    header: t('tracking.servicereq.handlingqty.label', 'Handling Unit Quantity'),
    description: t('receive.history.columns.bundleQuantities'),
    width: 'w-[180px]',
    cell: (row) => renderQuantities(row.bundleQuantities),
    enableSorting: false,
    visible: false
  },
  {
    id: 'reportingQuantities',
    header: t('tracking.servicereq.reportingqty.label', 'Reporting Unit Quantity'),
    description: t('receive.history.columns.reportingQuantities'),
    width: 'w-[180px]',
    cell: (row) => renderQuantities(row.reportingQuantities),
    enableSorting: false,
    visible: false
  },
  {
    id: 'expiryDates',
    header: t('requestline.expiryDate.label', 'Expiry Date'),
    description: t('receive.history.columns.expiryDates'),
    width: 'w-[120px]',
    cell: (row) =>
      row.expiryDates?.length ? (
        <div className="text-xs text-muted-foreground">
          {row.expiryDates.map((date) => formatDisplayDate(date)).join(', ')}
        </div>
      ) : (
        EMPTY_CELL
      ),
    enableSorting: false,
    visible: false
  },
  {
    id: 'temperatureRanges',
    header: t('tracking.servicereq.temprange.label', 'Temperature Range'),
    description: t('receive.history.columns.temperatureRanges'),
    width: 'w-[140px]',
    cell: (row) => renderTemperatureRanges(row.temperatureRanges),
    enableSorting: false,
    visible: false
  },
  {
    id: 'batchNumbers',
    header: t('requestline.batchNumber.label', 'Batch Number'),
    description: t('receive.history.columns.batchNumbers'),
    width: 'w-[130px]',
    cell: (row) => (row.batchNumbers?.length ? row.batchNumbers.join(', ') : EMPTY_CELL),
    enableSorting: false,
    visible: false
  },
  {
    id: 'senderOrganization',
    accessorKey: 'senderOrganization',
    header: t('newcmr.senderorg.label', 'Sender Organization'),
    description: t('receive.history.columns.senderOrganization'),
    width: 'w-[200px]',
    cell: (row) => row.senderOrganization || EMPTY_CELL,
    enableSorting: true,
    sortFieldId: 'senderOrg',
    visible: false
  },
  {
    id: 'receiverOrganization',
    accessorKey: 'receiverOrganization',
    header: t('newcmr.receiverorg.label', 'Receiver Organization'),
    description: t('receive.history.columns.receiverOrganization'),
    width: 'w-[200px]',
    cell: (row) => row.receiverOrganization || EMPTY_CELL,
    enableSorting: true,
    sortFieldId: 'receiverOrg',
    visible: false
  },
  {
    id: 'dangerousGoods',
    header: t('requestline.dangerousGoodsUnid.label', 'Dangerous Goods UN Identification number'),
    description: t('receive.history.columns.dangerousGoods'),
    width: 'w-[180px]',
    cell: (row) => renderDangerousGoods(row.dangerousGoods),
    enableSorting: false,
    visible: false
  },
  {
    id: 'ownersReference',
    accessorKey: 'ownersReference',
    header: t('request.ownersRef.label', "Owner's Reference"),
    description: "Owner's reference",
    width: 'w-[130px]',
    cell: (row) => row.ownersReference || EMPTY_CELL,
    enableSorting: true,
    sortFieldId: 'ownersRef',
    visible: false
  },
  {
    id: 'origin',
    accessorKey: 'origin',
    header: t('journey.origin.label', 'Origin Location'),
    description: t('receive.history.columns.ownersReference'),
    width: 'w-[230px]',
    cell: (row) => row.origin || EMPTY_CELL,
    enableSorting: true,
    sortFieldId: 'origin',
    visible: false
  },
  {
    id: 'totalValue',
    accessorKey: 'totalValue',
    header: t('tracking.servicereq.totval.label', 'Total Value (USD)'),
    description: t('receive.history.columns.totalValue'),
    width: 'w-[120px]',
    cell: (row) => (typeof row.totalValue === 'number' ? row.totalValue.toLocaleString() : EMPTY_CELL),
    enableSorting: false,
    visible: false
  },
  {
    id: 'subprojects',
    header: t('subproject.idnf.label', 'Sub-project code'),
    description: t('receive.history.columns.subprojects'),
    width: 'w-[130px]',
    cell: (row) => renderSubprojects(row.subprojects),
    enableSorting: false,
    visible: false
  },
  {
    id: 'attachments',
    header: t('newcmr.attachments.short', 'Attachments'),
    description: t('receive.history.columns.attachments'),
    width: 'w-[180px]',
    cell: (row) =>
      row.attachments?.length ? <ReceiveHistoryAttachmentsCell attachments={row.attachments} /> : EMPTY_CELL,
    visible: false,
    enableSorting: false
  }
];

/** Column presets — Medical View mirrors the JSF preset composition. */
export const getReceiveHistoryColumnPresets = (t: TFunction): ColumnPreset[] => [
  {
    id: 'medical-view',
    label: t('gen.customize.medview.button', 'Medical View'),
    columnIds: [
      'receiveDate',
      'consignmentNumber',
      'receivedAt',
      'cancellable',
      'documentNumber',
      'expiryDates',
      'temperatureRanges',
      'batchNumbers',
      'receiverOrganization',
      'totalValue'
    ]
  }
];
